import React, { useEffect, useState } from 'react';
import { CompanyDetailsInterface, Franchise } from '../services/exports/Interfaces';
import SearchIllustration from '../assets/illustrations/SearchIllustration';
import useTheme from '../hooks/ui/useTheme';
import { useTranslation } from 'react-i18next';
import InitialLoadingManager from '../services/api/InitialLoadingManager';
import toast from 'react-hot-toast';
import LoadingPage from '../components-library/loading/LoadingPage';
import WindowResource from '../services/resources/WindowResource';

export default function PageNotFound() {
  const { t } = useTranslation(null, { keyPrefix: 'Pages:PageNotFound' });

  const theme = useTheme();

  const [data, setData] = useState<Franchise>(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    loadData();
  }, []);

  // Loaded even when no franchise is in the store: this page is reached through a full-page
  // redirect, which can land before the persisted state was written. A host that resolves to no
  // franchise at all (404) is the plain not-found case, not a failure worth a toast.
  async function loadData() {
    const { success, response, status } = await InitialLoadingManager.getFranchiseData();
    setLoading(false);

    if (!success) {
      if (status !== 404) {
        toast.error(t('toasts.failed_to_load_stores'));
      }

      return;
    }

    setData(response?.data?.data);
  }

  // The backend owns each company's absolute shop URL (its franchise may sit on any root domain or a
  // custom FQDN). Until every backend serves it, fall back to the origin this 404 was reached on —
  // the visitor is already on the franchise's own host.
  function createUrl(company: CompanyDetailsInterface): string {
    return company?.web_shop_url || `${WindowResource.getOrigin()}/${company?.slug}`;
  }

  const renderDetails = () => {
    if (data?.companies?.length > 0) {
      return (
        <div className={wrapper}>
          <h2 className={titleStyle}>{t('title')}</h2>
          <h3 className={descriptionStyle}>{t('description')}</h3>
          {data.companies.map((company: CompanyDetailsInterface) => {
            const url = createUrl(company);

            return (
              <a href={url} key={url}>
                <h3 className={linkStyle}>{url}</h3>
              </a>
            );
          })}
        </div>
      );
    }

    if (data) {
      return (
        <div className={wrapper}>
          <h2 className={titleStyle}>{t('no_restaurants.title')}</h2>
          <h3 className={descriptionStyle}>{t('no_restaurants.description')}</h3>
        </div>
      );
    }

    return (
      <div className={wrapper}>
        <h2 className={titleStyle}>{t('not_found')}</h2>
      </div>
    );
  };

  return (
    <>
      {loading && <LoadingPage />}
      <SearchIllustration className={illustrationStyle} color={theme.color.primary} />
      {renderDetails()}
    </>
  );
}

const wrapper = 'flex flex-col w-full bg-background-inkWhite_white1 items-center lg:w-2/3 w-full mx-auto px-md';

const titleStyle = 'mb-md text-center';

const descriptionStyle = 'text-primary mb-md text-center';

const linkStyle = 'text-center text-primary underline underline-offset-4 decoration-solid mb-md';

const illustrationStyle = 'lg:w-1/4 md:w-1/2 w-3/4 mx-auto my-lg';
